import React, { useState, useEffect, useRef } from "react";

const ImageUploadField = ({ title, propertyName, values, setFieldValue }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      console.log("fileee", file);

      setSelectedImage(file);
      setFieldValue(propertyName, file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedImage(file);
      setFieldValue(propertyName, file);
    }
  };

  const handleRemove = () => {
    setSelectedImage(null);
    setFieldValue(propertyName, null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <div className="w-full">
      {title && (
        <label className="block text-slate-700 text-sm font-semibold mb-3">
          {title}
        </label>
      )}
      
      {!values[propertyName] ? (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            relative border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer
            transition-all duration-300 ease-in-out
            ${isDragging 
              ? 'border-primary2 bg-primary2/5 scale-[1.02]' 
              : 'border-slate-200 hover:border-primary2/50 hover:bg-slate-50'
            }
          `}
        >
          <input
            ref={inputRef}
            type="file"
            id={`imageUpload-${propertyName}`}
            accept="image/*"
            onChange={handleImageChange}
            className="hidden"
          />
          
          <div className="flex flex-col items-center gap-3">
            <div className={`
              w-16 h-16 rounded-2xl flex items-center justify-center
              transition-all duration-300
              ${isDragging 
                ? 'bg-primary2/10' 
                : 'bg-gradient-to-br from-slate-100 to-slate-50'
              }
            `}>
              <svg 
                className={`w-8 h-8 transition-colors duration-300 ${isDragging ? 'text-primary2' : 'text-slate-400'}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            
            <div>
              <p className="text-sm font-medium text-slate-600">
                <span className="text-primary2 hover:text-primary2/80">Click to upload</span>
                {" "}or drag and drop
              </p>
              <p className="text-xs text-slate-400 mt-1">PNG, JPG, GIF up to 10MB</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="relative group">
          <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-slate-100 to-slate-50 p-2">
            <img
              src={URL.createObjectURL(values[propertyName])}
              alt="Selected"
              className="w-full h-48 object-contain rounded-xl"
            />
          </div>
          
          {/* Overlay with actions */}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="p-3 bg-white rounded-xl hover:bg-slate-100 transition-colors shadow-lg"
              title="Change image"
            >
              <svg className="w-5 h-5 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>
            <button
              type="button"
              onClick={handleRemove}
              className="p-3 bg-red-500 rounded-xl hover:bg-red-600 transition-colors shadow-lg"
              title="Remove image"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
          
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="hidden"
          />
          
          {/* File name badge */}
          <div className="absolute bottom-4 left-4 right-4">
            <div className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm">
              <p className="text-xs text-slate-500 truncate">
                {values[propertyName]?.name || 'Selected image'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploadField;
